import React, { useEffect, useState } from "react";
import { debounce, uniqueId, map, trim } from "lodash";
import classNames from "classnames";

import { Row, Col, Space } from "antd";
import Icon, {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { SelectInput, TextInput, DatePicker } from "@life-line-lab/components-react";

import "./data-table-filter.scss";

export interface FilterSelectionProps {
  type?: string;
  key: string;
  label?: string;
  name: string;
  value: string | boolean;
  options: Array<{ label: string; value: string }>;
  placeholder?: string;
  isReadonly?: boolean;
  onClear?: () => void;
}

interface DataTableFilterProps {
  filterLabel?: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  renderFilterSelection?: FilterSelectionProps[];
  hasSearch?: boolean;
  onDelete?: () => void;
  onUpdate?: () => void;
  onExpand?: () => void;
  onMerge?: () => void;
  onSearch?: (text: string) => void;
  onFilter?: (filters: { [key: string]: string | boolean }) => void;
}

const DataTableFilter: React.FC<DataTableFilterProps> = ({
  filterLabel,
  searchLabel,
  searchPlaceholder,
  renderFilterSelection,
  hasSearch = true,
  onDelete,
  onUpdate,
  onExpand,
  onMerge,
  onSearch,
  onFilter,
}) => {
  const filterId = uniqueId("data-table-filter--filter-dropdown-");

  const [isActiveSearch, setIsActiveSearch] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string }>({});
  const debounced = onSearch ? debounce(onSearch, 500) : () => {};

  useEffect(() => {
    if (renderFilterSelection) {
      let initialFilters = {};
      renderFilterSelection.map((filter) => {
        initialFilters[filter.name] = filter.value;
      });
      setSelectedFilters(initialFilters);
    }
  }, [renderFilterSelection]);

  const delayedQuery = (value: string) => {
    return debounced(value);
  };

  const handleChangeSearchBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = trim(e.target.value);
    delayedQuery(value);
  };

  const handleSelectChange = (value: string, name: string) => {
    const updatedFilters = { ...selectedFilters };
    updatedFilters[name] = value;
    setSelectedFilters(updatedFilters);
    if (onFilter) onFilter(updatedFilters);
  };

  return (
    <React.Fragment>
      <div className="data-table-filter">
        {hasSearch && (
          <TextInput
            placeholder={searchPlaceholder}
            size="large"
            onChange={handleChangeSearchBox}
            suffix={<SearchOutlined />}
          />
        )}
        {filterLabel ? <span className="pl-20">{filterLabel}</span> : null}

        {renderFilterSelection?.length ? (
          <div className="filter-section">
            {renderFilterSelection?.map((filter) => {
              return (
                <React.Fragment key={filter.key}>
                  {filter.type == "date" ? (
                    <DatePicker
                      allowClear
                      placement="topRight"
                      label={filter.label || ""}
                      placeholder={filter.placeholder}
                      style={{ width: 150 }}
                      isReadonly={filter.isReadonly}
                      value={selectedFilters[filter.name]}
                      onChange={(value: any) => {
                        handleSelectChange(value, filter.name);
                      }}
                    />
                  ) : (
                    <SelectInput
                      showSearch
                      filterOption={(input, option) =>
                        option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                      placeholder={filter.placeholder}
                      style={{ width: 150 }}
                      label={filter.label || ""}
                      options={filter.options}
                      value={selectedFilters[filter.name]}
                      isReadonly={filter.isReadonly}
                      onChange={(value: any) => handleSelectChange(value, filter.name)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : null}
      </div>
    </React.Fragment>
  );
};

// interface StatusFilterProps {
//   label?: string;
//   currentValue?: string;
//   initialStatus?: string;
//   options: Array<{ label: string; value: string; }>;
//   onSelectStatus: (value: string | boolean) => void;
// }

// const StatusFilter: React.FC<StatusFilterProps> = ({ label, options, initialStatus, currentValue, onSelectStatus }) => {
//   const [selectedStatus, setSelectedStatus] = useState<string | boolean>(initialStatus || '');

//   useEffect(() => {
//     if (currentValue)
//       setSelectedStatus(currentValue)
//   }, [currentValue])

//   const handleStatusClick = (value: string) => {
//     setSelectedStatus(value);
//     onSelectStatus(value);
//   }

//   return (
//     <div className="status-filter">
//       <div className="label">{label}</div>
//       <div className="tags">
//         {options && map(options, (option, index) => {
//           const key = option.value ? option.value : 'DEFAULT';

//           return (
//             <span
//               key={index}
//               onClick={() => handleStatusClick(option.value)}
//               className={
//                 classNames('filter-tag', {
//                   'selected': selectedStatus === option.value,
//                   [key]: selectedStatus === option.value,
//                 })
//               }
//             >
//               {option.label}
//             </span>
//           )
//         })}
//       </div>
//     </div>
//   )
// }

export default DataTableFilter;
